import { useMemo, useState } from "react";
import { Loader2, Play, RefreshCw, Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DataTable,
  PageHeader,
  type DataTableColumn,
} from "@/components/shared";
import { useToast } from "@/hooks/use-toast";
import {
  useAdminCommissionDistribution,
  useDistributeCommissionOrderMutation,
  useRunDueCommissionDistributionMutation,
} from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import type {
  CommissionDistributionItemDto,
  CommissionDistributionStatusFilter,
} from "@/types/income.dto";

function formatWhen(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN");
}

const AdminCommissionDistribution = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState<CommissionDistributionStatusFilter>("ready");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const filter = useMemo(
    () => ({ status, search: search.trim() || undefined, page, pageSize }),
    [status, search, page],
  );

  const { data, isLoading, isFetching, refetch } = useAdminCommissionDistribution(filter);
  const runMutation = useRunDueCommissionDistributionMutation();
  const distributeMutation = useDistributeCommissionOrderMutation();

  const rows = data?.items ?? [];

  const columns: DataTableColumn<CommissionDistributionItemDto>[] = [
    {
      id: "order",
      header: "Order",
      cell: (row) => (
        <div>
          <p className="font-mono text-sm font-semibold">{row.orderNumber}</p>
          <p className="text-xs text-muted-foreground">{row.storeName}</p>
        </div>
      ),
    },
    {
      id: "customer",
      header: "Customer",
      cell: (row) => (
        <div>
          <p className="text-sm font-medium">{row.customerName}</p>
          <p className="text-xs text-muted-foreground font-mono">
            {row.customerMemberCode || "—"}
          </p>
        </div>
      ),
    },
    {
      id: "bv",
      header: "Order BV",
      align: "right",
      cell: (row) => <span className="tabular-nums font-medium">{row.orderBv}</span>,
    },
    {
      id: "total",
      header: "Total",
      align: "right",
      cell: (row) => <span className="tabular-nums">₹{row.orderTotal}</span>,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <div className="space-y-1">
          <Badge variant={row.isReady ? "default" : "secondary"} className="text-xs">
            {row.isReady ? "Ready" : row.incomeStatusText}
          </Badge>
          {row.hoursRemaining != null ? (
            <p className="text-[11px] text-muted-foreground">{row.hoursRemaining}h left</p>
          ) : null}
          {row.incomeLastError ? (
            <p className="max-w-[180px] truncate text-[11px] text-destructive" title={row.incomeLastError}>
              {row.incomeLastError}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      id: "timeline",
      header: "Timeline",
      cell: (row) => (
        <div className="text-xs text-muted-foreground space-y-0.5">
          <p>Delivered: {formatWhen(row.deliveredAt)}</p>
          <p>Eligible: {formatWhen(row.incomeEligibleAt)}</p>
          <p>Paid: {formatWhen(row.incomeDistributedAt)}</p>
        </div>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          {(row.isReady || row.incomeStatusText === "Failed") && (
            <Button
              size="sm"
              variant="outline"
              disabled={distributeMutation.isPending}
              onClick={() =>
                void distributeMutation
                  .mutateAsync({ orderId: row.orderId })
                  .then(() => toast({ title: "Commission distributed", description: row.orderNumber }))
                  .catch((error) =>
                    toast({
                      title: "Distribute failed",
                      description: getApiErrorMessage(error),
                      variant: "destructive",
                    }),
                  )
              }
            >
              Distribute
            </Button>
          )}
          {row.incomeStatusText === "Held" && !row.isReady ? (
            <Button
              size="sm"
              variant="ghost"
              disabled={distributeMutation.isPending}
              onClick={() => {
                if (!confirm(`Force distribute ${row.orderNumber} before return window ends?`)) return;
                void distributeMutation
                  .mutateAsync({ orderId: row.orderId, force: true })
                  .then(() => toast({ title: "Force distributed", description: row.orderNumber }))
                  .catch((error) =>
                    toast({
                      title: "Force distribute failed",
                      description: getApiErrorMessage(error),
                      variant: "destructive",
                    }),
                  );
              }}
            >
              <Zap className="mr-1 h-3.5 w-3.5" /> Force
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Commission Distribution"
        description="Commission is held until the product return window ends, then paid automatically. Ready orders can be distributed now."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() =>
                void runMutation
                  .mutateAsync()
                  .then((result) =>
                    toast({
                      title: "Distribution run complete",
                      description: `Processed ${result.processed} · Paid ${result.distributed} · Skipped ${result.skipped} · Failed ${result.failed}`,
                    }),
                  )
                  .catch((error) =>
                    toast({
                      title: "Run failed",
                      description: getApiErrorMessage(error),
                      variant: "destructive",
                    }),
                  )
              }
              disabled={runMutation.isPending}
            >
              {runMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Play className="mr-1.5 h-3.5 w-3.5" />
              )}
              Run due now
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3">
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as CommissionDistributionStatusFilter);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[160px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ready">Ready to pay</SelectItem>
            <SelectItem value="held">In return window</SelectItem>
            <SelectItem value="distributed">Distributed</SelectItem>
            <SelectItem value="skipped">Skipped</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search order / customer / store"
          className="h-9 max-w-xs"
        />
      </div>

      <DataTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.orderId)}
        loading={isLoading}
        emptyMessage="No orders in this commission queue."
        hidePagination
      />

      {(data?.totalPages ?? 1) > 1 ? (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} / {data?.totalPages ?? 1}
          </span>
          <Button
            size="sm"
            variant="outline"
            disabled={page >= (data?.totalPages ?? 1)}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default AdminCommissionDistribution;
